<template>
  <div class="modal-overlay" v-if="isVisible" @click.self="close">
    <div class="article-details bg-blue-color">
      <div class="details-header">
        <h3>{{ articleData.title }}</h3>
        <button class="close-details" @click="close">✕</button>
      </div>

      <div class="scrollable-details-area">
        <!-- Section Références -->
        <div class="ref-section" v-if="hasReferencesComputed">
          <h4 class="ref-title">{{ $t('modals.articleDetail.sections.references') }}</h4>

          <!-- Juritext references -->
          <div v-if="articleData.source === 'juritext'" class="ref-content">
            <div class="decisions-links">
              <a :href="officialDecisionLink" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                {{ $t('modals.articleDetail.actions.viewOnOfficialSite') }}
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Legiarti references -->
          <div v-if="articleData.source === 'legiarti'" class="ref-content">
            <div v-if="articleData.id" class="source-url">
              <a :href="`https://www.legifrance.gouv.fr/codes/article_lc/${articleData.id}`" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnLegifrance') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Bofip references -->
          <div v-if="articleData.source === 'bofip'" class="ref-content">
            <div v-if="articleData.sourceData && articleData.sourceData.identifiant" class="balo-info-item">
              <strong>{{ $t('modals.articleDetail.fields.identifier') }}</strong>
              <span class="balo-siren">{{ articleData.sourceData.identifiant }}</span>
            </div>
            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnOfficialSite') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- CNN references -->
          <div v-if="articleData.source === 'cnn'" class="ref-content">
            <div v-if="articleData.article_id" class="cnn-reference">
              <strong>{{ $t('modals.articleDetail.fields.reference') }}</strong>
              <a :href="`https://www.legifrance.gouv.fr/conv_coll/article/${articleData.article_id}`" target="_blank" rel="noopener noreferrer" class="legifrance-link" style="display: flex; align-items: center; color: #625CFB;">
                {{ $t('modals.articleDetail.actions.legifrance') }}
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- BALO references -->
          <div v-if="articleData.source === 'balo'" class="ref-content">
            <div v-if="articleData.id_annonce" class="balo-info-item">
              <strong>{{ $t('modals.articleDetail.fields.reference') }}</strong>
              <a :href="baloOfficialLink" target="_blank" rel="noopener noreferrer" class="balo-link" style="display: flex; align-items: center; color: #625CFB;">
                {{ $t('modals.articleDetail.actions.viewOnBaloOfficial') }}
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Euritext references -->
          <div v-if="articleData.source === 'euritext'" class="ref-content">
            <div v-if="articleData.sourceData && articleData.sourceData.links">
              <div class="euritext-links">
                <strong>{{ $t('modals.articleDetail.fields.relatedDocuments') }}</strong>
                <div v-for="(url, title, index) in articleData.sourceData.links" :key="index" class="euritext-link">
                  <a :href="url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                    {{ formatEuritextTitle(title) }}
                    <ExternalLinkIcon />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Statuts references -->
          <div v-if="articleData.source === 'statuts'" class="ref-content">
            <div v-if="articleData.slugged_title" class="statuts-info-item">
              <strong>{{ $t('modals.articleDetail.fields.document') }}</strong>
              <a :href="`https://decisions.query-juriste.com/${articleData.slugged_title}`" target="_blank" rel="noopener noreferrer" class="statuts-link" style="display: flex; align-items: center; color: #625CFB;">
                {{ $t('modals.articleDetail.actions.viewFullDocument') }}
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Convbi references -->
          <div v-if="articleData.source === 'convbi'" class="ref-content">
            <div v-if="articleData.pdf_url" class="source-url">
              <a :href="articleData.pdf_url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewConventionPdf') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Infofi references -->
          <div v-if="articleData.source === 'infofi'" class="ref-content">
            <div v-if="articleData.sourceData && articleData.sourceData.Lien_Document" class="source-url">
              <a :href="articleData.sourceData.Lien_Document" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewSourceDocumentPdf') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Reglements references -->
          <div v-if="articleData.source === 'reglements'" class="ref-content">
            <div v-if="articleData.celex" class="reglements-info-item">
              <strong>{{ $t('modals.articleDetail.fields.celex') }}</strong>
              <a :href="`https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX:${articleData.celex}`"
                target="_blank" rel="noopener noreferrer"
                style="display: flex; align-items: center; color: #625CFB;">
                {{ $t('modals.articleDetail.actions.viewOnEurLex') }}
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- BODACC references -->
          <div v-if="articleData.source === 'bodacc'" class="ref-content">
            <div v-if="articleData.url_complete" class="source-url">
              <a :href="articleData.url_complete" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnBodaccOfficial') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Energy references -->
          <div v-if="articleData.source === 'energy'" class="ref-content">
            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnSite') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Actes Societes references -->
          <div v-if="articleData.source === 'actes_societes'" class="ref-content">
            <div v-if="articleData.siren" class="actes-societes-info-item">
              <strong>{{ $t('modals.articleDetail.fields.siren') }}</strong>
              <span class="actes-societes-siren-value">{{ articleData.siren }}</span>
            </div>
            <div v-if="articleData.date_acte" class="actes-societes-info-item">
              <strong>{{ $t('modals.articleDetail.fields.acteDate') }}</strong>
              <span class="actes-societes-date-full">{{ formatDate(articleData.date_acte) }}</span>
            </div>
            <div v-if="actesSocietesSourcePath" class="source-url">
              <a :href="getActesSocietesFullUrl(actesSocietesSourcePath)" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewActePdf') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Concurrence references -->
          <div v-if="articleData.source === 'concurrence'" class="ref-content">
            <div v-if="articleData.numero_affaire" class="concurrence-info-item">
              <strong>{{ $t('modals.articleDetail.fields.caseNumber') }}</strong>
              <span class="concurrence-affaire">{{ articleData.numero_affaire }}</span>
            </div>
            <div v-if="articleData.url_page" class="source-url">
              <a :href="articleData.url_page" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnConcurrenceAuthority') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- INPI references -->
          <div v-if="articleData.source === 'inpi'" class="ref-content">
            <div v-if="articleData.reference" class="inpi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.reference') }}</strong>
              <span class="inpi-reference">{{ articleData.reference }}</span>
            </div>
            <div v-if="articleData.url_document_final" class="source-url">
              <a :href="articleData.url_document_final" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewDecisionOnInpi') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- CNIL references -->
          <div v-if="articleData.source === 'cnil'" class="ref-content">
            <div v-if="articleData.id" class="cnil-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.identifier') }}</strong>
              <span>{{ articleData.id }}</span>
            </div>
            <div v-if="articleData.url || (articleData.sourceData && articleData.sourceData.link)" class="source-url">
              <a :href="articleData.url || articleData.sourceData.link" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnCnil') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- BOSS references -->
          <div v-if="articleData.source === 'boss'" class="ref-content">
            <div v-if="articleData.url || (articleData.sourceData && (articleData.sourceData.link || articleData.sourceData.url))" class="source-url">
              <a :href="articleData.url || (articleData.sourceData && (articleData.sourceData.link || articleData.sourceData.url))" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnBoss') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- ACPR references -->
          <div v-if="articleData.source === 'acpr'" class="ref-content">
            <div v-if="articleData.id" class="acpr-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.identifier') }}</strong>
              <span>{{ articleData.id }}</span>
            </div>
            <div v-if="articleData.numero" class="acpr-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.number') }}</strong>
              <span>{{ articleData.numero }}</span>
            </div>
            <div v-if="articleData.url || (articleData.sourceData && articleData.sourceData.link)" class="source-url">
              <a :href="articleData.url || articleData.sourceData.link" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnAcpr') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- AMF references -->
          <div v-if="articleData.source === 'amf'" class="ref-content">
            <div v-if="articleData.reference" class="amf-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.reference') }}</strong>
              <span>{{ articleData.reference }}</span>
            </div>
            <div v-if="articleData.url || (articleData.sourceData && articleData.sourceData.link)" class="source-url">
              <a :href="articleData.url || articleData.sourceData.link" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnAmf') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- ECCOMP (Commission Européenne - Concurrence) references -->
          <div v-if="articleData.source === 'eccomp'" class="ref-content">
            <div v-if="articleData.case_number" class="eccomp-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.caseNumber') }}</strong>
              <span>{{ articleData.case_number }}</span>
            </div>
            <div v-if="articleData.instrument" class="eccomp-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.instrument') }}</strong>
              <span>{{ articleData.instrument }}</span>
            </div>
            <div v-if="articleData.decision_reference" class="eccomp-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.decisionReference') }}</strong>
              <span>{{ articleData.decision_reference }}</span>
            </div>
            <div v-if="articleData.url || (articleData.sourceData && articleData.sourceData.link)" class="source-url">
              <a :href="articleData.url || articleData.sourceData.link" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnEccomp') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- UPC (Juridiction unifiée du brevet) references -->
          <div v-if="articleData.source === 'upc'" class="ref-content">
            <div v-if="articleData.case_numbers && articleData.case_numbers.length" class="upc-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.caseNumber') }}</strong>
              <span>{{ articleData.case_numbers.join(', ') }}</span>
            </div>
            <div v-if="articleData.court" class="upc-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.court') }}</strong>
              <span>{{ articleData.court }}</span>
            </div>
            <div v-if="articleData.action_type" class="upc-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.actionType') }}</strong>
              <span>{{ articleData.action_type }}</span>
            </div>
            <div v-if="articleData.detail_url || (articleData.sourceData && articleData.sourceData.detail_url)" class="source-url">
              <a :href="articleData.detail_url || articleData.sourceData.detail_url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnUpc') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
            <div v-if="articleData.pdf_url || (articleData.sourceData && articleData.sourceData.pdf_url)" class="source-url">
              <a :href="articleData.pdf_url || articleData.sourceData.pdf_url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewUpcPdf') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- EDPB (European Data Protection Board) references -->
          <div v-if="articleData.source === 'edpb'" class="ref-content">
            <div v-if="articleData.publication_types && articleData.publication_types.length" class="edpb-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.publicationType') }}</strong>
              <span>{{ articleData.publication_types.join(', ') }}</span>
            </div>
            <div v-if="articleData.topics && articleData.topics.length" class="edpb-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.topics') }}</strong>
              <span>{{ articleData.topics.join(', ') }}</span>
            </div>
            <div v-if="articleData.date" class="edpb-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span>{{ articleData.date }}</span>
            </div>
            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnEdpb') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
            <div v-if="articleData.pdf_url_used" class="source-url">
              <a :href="articleData.pdf_url_used" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewEdpbPdf') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- AGCM references -->
          <div v-if="articleData.source === 'agcm'" class="ref-content">
            <div v-if="articleData.date" class="agcm-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.dataIt') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnAgcm') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Giustizia Amministrativa references -->
          <div v-if="articleData.source === 'giustizia_amministrativa'" class="ref-content">
            <div v-if="articleData.court" class="ga-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.giudice') }}</strong>
              <span>{{ articleData.court }}</span>
            </div>
            <div v-if="articleData.tipo" class="ga-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.tipo') }}</strong>
              <span>{{ articleData.tipo }}</span>
            </div>
            <div v-if="articleData.date" class="ga-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.dataIt') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnGiustiziaAmministrativa') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Jurisprudence Europe Italy references -->
          <div v-if="articleData.source === 'jurisprudence_europe_italy'" class="ref-content">
            <div v-if="articleData.court" class="euit-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.giudice') }}</strong>
              <span>{{ articleData.court }}</span>
            </div>
            <div v-if="articleData.celex" class="euit-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.celex') }}</strong>
              <span>{{ articleData.celex }}</span>
            </div>
            <div v-if="articleData.ecli" class="euit-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.ecli') }}</strong>
              <span>{{ articleData.ecli }}</span>
            </div>
            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnEurLexIt') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- EUR-Lex Italia references (Regolamenti e Direttive UE) -->
          <div v-if="articleData.source === 'eurlex_italy'" class="ref-content">
            <div v-if="articleData.breadcrumb" class="eurlex-ref-item eurlex-breadcrumb">
              <strong>{{ $t('modals.articleDetail.fields.struttura') }}</strong>
              <span>{{ articleData.breadcrumb }}</span>
            </div>
            <div v-if="articleData.article_heading" class="eurlex-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.articolo') }}</strong>
              <span>{{ articleData.article_heading }}<span v-if="articleData.article_title"> — {{ articleData.article_title }}</span></span>
            </div>
            <div v-if="articleData.cons_celex" class="eurlex-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.celexConsolidato') }}</strong>
              <span>{{ articleData.cons_celex }}</span>
            </div>
            <div v-if="articleData.consolidation_date" class="eurlex-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.dataConsolidamento') }}</strong>
              <span>{{ articleData.consolidation_date }}</span>
            </div>
            <div v-if="articleData.act_title" class="eurlex-ref-item">
              <strong>{{ $t('modals.articleDetail.fields.attoDiBase') }}</strong>
              <span>{{ articleData.act_title }}</span>
            </div>
            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; gap: 6px;">
                <strong>{{ $t('modals.articleDetail.actions.viewOnEurLexIt') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>

          <!-- Mention references -->
          <div v-if="articleData.source === 'mention'" class="ref-content">
            <div v-if="articleData.original_reference" class="mention-info-item">
              <strong>{{ $t('modals.articleDetail.fields.originalReference') }}</strong>
              <span>{{ articleData.original_reference }}</span>
            </div>

            <div v-if="articleData.url" class="source-url">
              <a :href="articleData.url" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; color: #625CFB;">
                <strong>{{ $t('modals.articleDetail.actions.viewOfficialDocument') }}</strong>
                <ExternalLinkIcon />
              </a>
            </div>
          </div>
        </div>        <!-- Section Citations -->
        <div class="citations-section" v-if="hasCitationsComputed">
          <h4 class="citations-title">{{ $t('modals.articleDetail.sections.citations') }}</h4>
          <div class="citation-list">
            <div v-for="(citation, idx) in articleData.sourceData.citations" :key="idx" class="citation-item">
              <strong v-if="citation.theme">{{ citation.theme }}:</strong>
              <p class="citation-text">"{{ citation.citation || citation }}"</p>
            </div>
          </div>
        </div>

        <!-- Metadata sections based on source type -->
        <!-- CNN Metadata -->
        <div class="details-content" v-if="articleData.source === 'cnn'">
          <div class="cnn-metadata">
            <div v-if="articleData.etat" class="cnn-status">
              <strong>{{ $t('modals.articleDetail.fields.etat') }}</strong>
              <span :class="getEtatClass(articleData.etat)">{{ formatEtat(articleData.etat) }}</span>
            </div>
            <div v-if="articleData.date_debut || articleData.date_fin" class="cnn-validity">
              <strong>{{ $t('modals.articleDetail.fields.inForceSince') }}</strong>
              <span v-if="articleData.date_debut">{{ articleData.date_debut }}</span>
            </div>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- BALO Metadata -->
        <div class="details-content" v-else-if="articleData.source === 'balo'">
          <div class="balo-metadata">
            <div v-if="articleData.societes_noms" class="balo-info-item">
              <strong>{{ $t('modals.articleDetail.fields.companies') }}</strong>
              <span class="balo-company-name">{{ articleData.societes_noms }}</span>
            </div>
            <div v-if="articleData.siren" class="balo-info-item">
              <strong>{{ $t('modals.articleDetail.fields.siren') }}</strong>
              <span class="balo-siren">{{ articleData.siren }}</span>
            </div>
            <div v-if="articleData.dateparution" class="balo-info-item">
              <strong>{{ $t('modals.articleDetail.fields.publicationDate') }}</strong>
              <span class="balo-date">{{ formatDate(articleData.dateparution) }}</span>
            </div>
            <div v-if="articleData.categorie_libelle" class="balo-info-item">
              <strong>{{ $t('modals.articleDetail.fields.category') }}</strong>
              <span class="balo-category-badge">{{ formatBaloCategory(articleData.categorie_libelle) }}</span>
            </div>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- Legiarti -->
        <div class="details-content" v-else-if="articleData.source === 'legiarti'">
          <div v-if="articleData.hierarchy" class="hierarchy">
            <strong>{{ formatHierarchy(articleData.hierarchy) }}</strong>
          </div>
          <div v-html="formattedContent"></div>
          <div v-if="articleData.annotation" class="annotation-section">
            <h4 class="annotation-title">{{ $t('modals.articleDetail.sections.annotation') }}</h4>
            <div v-html="formattedAnnotation" class="annotation-content"></div>
          </div>
        </div>

        <!-- Reglements -->
        <div class="details-content" v-else-if="articleData.source === 'reglements'">
          <div class="reglements-metadata">
            <div v-if="articleData.date" class="reglements-info-item">
              <strong>{{ $t('modals.articleDetail.fields.regulationDate') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.chapter_title" class="reglements-info-item">
              <strong>{{ $t('modals.articleDetail.fields.chapter') }}</strong>
              <span>{{ articleData.chapter_title }}</span>
            </div>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- BODACC -->
        <div class="details-content" v-else-if="articleData.source === 'bodacc'">
          <div class="bodacc-metadata">
            <div v-if="articleData.familleavis" class="bodacc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.noticeFamily') }}</strong>
              <span class="bodacc-family-badge">{{ articleData.familleavis }}</span>
            </div>
            <div v-if="articleData.typeavis" class="bodacc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.noticeType') }}</strong>
              <span class="bodacc-type-badge">{{ articleData.typeavis }}</span>
            </div>
            <div v-if="articleData.dateparution" class="bodacc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.publicationDate') }}</strong>
              <span class="bodacc-date">{{ formatDate(articleData.dateparution) }}</span>
            </div>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- Actes Societes -->
        <div class="details-content" v-else-if="articleData.source === 'actes_societes'">
          <div v-if="articleData.summary" class="actes-societes-summary-section">
            <h4 class="summary-title">{{ $t('modals.articleDetail.sections.acteSummary') }}</h4>
            <div v-html="formatText(articleData.summary)" class="summary-content"></div>
          </div>
          <div v-if="articleData.content" class="actes-societes-content-section">
            <h4 class="content-section-title">{{ $t('modals.articleDetail.sections.acteContent') }}</h4>
            <div v-html="formattedContent"></div>
          </div>
        </div>

        <!-- Infofi -->
        <div class="details-content" v-else-if="articleData.source === 'infofi'">
          <div class="infofi-metadata">
            <div v-if="articleData.nom_societe" class="infofi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.company') }}</strong>
              <span class="infofi-company-name">{{ articleData.nom_societe }}</span>
            </div>
            <div v-if="articleData['Titre opération']" class="infofi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.operationTitle') }}</strong>
              <span>{{ articleData['Titre opération'] }}</span>
            </div>
            <div v-if="articleData.date_document" class="infofi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.documentDate') }}</strong>
              <span class="infofi-doc-date">{{ formatDate(articleData.date_document) }}</span>
            </div>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- Concurrence -->
        <div class="details-content" v-else-if="articleData.source === 'concurrence'">
          <div class="concurrence-metadata">
            <div v-if="articleData.type_document" class="concurrence-info-item">
              <strong>{{ $t('modals.articleDetail.fields.type') }}</strong>
              <span class="concurrence-type-badge">{{ articleData.type_document }}</span>
            </div>
            <div v-if="articleData.numero_affaire" class="concurrence-info-item">
              <strong>{{ $t('modals.articleDetail.fields.caseNumber') }}</strong>
              <span class="concurrence-affaire-num">{{ articleData.numero_affaire }}</span>
            </div>
            <div v-if="articleData.date_decision" class="concurrence-info-item">
              <strong>{{ $t('modals.articleDetail.fields.decisionDate') }}</strong>
              <span class="concurrence-date-full">{{ formatDate(articleData.date_decision) }}</span>
            </div>
            <div v-if="articleData.secteur" class="concurrence-info-item">
              <strong>{{ $t('modals.articleDetail.fields.sector') }}</strong>
              <span class="concurrence-sector-name">{{ articleData.secteur }}</span>
            </div>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- INPI -->
        <div class="details-content" v-else-if="articleData.source === 'inpi'">
          <div class="inpi-metadata">
            <div v-if="articleData.domaine" class="inpi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.domain') }}</strong>
              <span class="inpi-domain-badge">{{ articleData.domaine }}</span>
            </div>
            <div v-if="articleData.reference" class="inpi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.reference') }}</strong>
              <span class="inpi-reference-num">{{ articleData.reference }}</span>
            </div>
            <div v-if="articleData.date" class="inpi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span class="inpi-date-full">{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.juridiction" class="inpi-info-item">
              <strong>{{ $t('modals.articleDetail.fields.jurisdiction') }}</strong>
              <span class="inpi-jurisdiction-name">{{ articleData.juridiction }}</span>
            </div>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- CNIL -->
        <div class="details-content" v-else-if="articleData.source === 'cnil'">
          <div class="cnil-metadata">
            <div v-if="articleData.nature" class="cnil-info-item">
              <strong>{{ $t('modals.articleDetail.fields.nature') }}</strong>
              <span class="cnil-nature">{{ articleData.nature }}</span>
            </div>
            <div v-if="articleData.date" class="cnil-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span class="cnil-date-full">{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.titre_complet" class="cnil-info-item">
              <strong>{{ $t('modals.articleDetail.fields.fullTitle') }}</strong>
              <span class="cnil-full-title">{{ articleData.titre_complet }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.cnilSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- BOSS -->
        <div class="details-content" v-else-if="articleData.source === 'boss'">
          <div class="boss-metadata">
            <div v-if="articleData.date" class="boss-info-item">
              <strong>{{ $t('modals.articleDetail.fields.lastUpdateDate') }}</strong>
              <span class="boss-date-full">{{ articleData.date ? new Date(articleData.date).getFullYear() : '' }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.bossContent') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- ACPR -->
        <div class="details-content" v-else-if="articleData.source === 'acpr'">
          <div class="acpr-metadata">
            <div v-if="articleData.acpr_source" class="acpr-info-item">
              <strong>{{ $t('modals.articleDetail.fields.source') }}</strong>
              <span class="acpr-source-badge">{{ articleData.acpr_source }}</span>
            </div>
            <div v-if="articleData.doc_subtype" class="acpr-info-item">
              <strong>{{ $t('modals.articleDetail.fields.type') }}</strong>
              <span>{{ articleData.doc_subtype }}</span>
            </div>
            <div v-if="articleData.date" class="acpr-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.categorie" class="acpr-info-item">
              <strong>{{ $t('modals.articleDetail.fields.category') }}</strong>
              <span>{{ articleData.categorie }}</span>
            </div>
            <div v-if="articleData.themes" class="acpr-info-item">
              <strong>{{ $t('modals.articleDetail.fields.themes') }}</strong>
              <span>{{ articleData.themes }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.acprSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- AMF -->
        <div class="details-content" v-else-if="articleData.source === 'amf'">
          <div class="amf-metadata">
            <div v-if="articleData.amf_source" class="amf-info-item">
              <strong>{{ $t('modals.articleDetail.fields.source') }}</strong>
              <span class="amf-source-badge">{{ articleData.amf_source }}</span>
            </div>
            <div v-if="articleData.doc_type" class="amf-info-item">
              <strong>{{ $t('modals.articleDetail.fields.type') }}</strong>
              <span>{{ articleData.doc_type }}</span>
            </div>
            <div v-if="articleData.reference" class="amf-info-item">
              <strong>{{ $t('modals.articleDetail.fields.reference') }}</strong>
              <span>{{ articleData.reference }}</span>
            </div>
            <div v-if="articleData.date" class="amf-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.themes" class="amf-info-item">
              <strong>{{ $t('modals.articleDetail.fields.themes') }}</strong>
              <span>{{ articleData.themes }}</span>
            </div>
            <div v-if="articleData.montant" class="amf-info-item">
              <strong>{{ $t('modals.articleDetail.fields.amount') }}</strong>
              <span>{{ articleData.montant }}</span>
            </div>
            <div v-if="articleData.sanctioned_entity" class="amf-info-item">
              <strong>{{ $t('modals.articleDetail.fields.sanctionedEntity') }}</strong>
              <span>{{ articleData.sanctioned_entity }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.amfSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- ECCOMP (Commission Européenne - Concurrence) -->
        <div class="details-content" v-else-if="articleData.source === 'eccomp'">
          <div class="eccomp-metadata">
            <div v-if="articleData.instrument" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.instrument') }}</strong>
              <span class="eccomp-instrument-badge">{{ articleData.instrument }}</span>
            </div>
            <div v-if="articleData.case_number" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.caseNumber') }}</strong>
              <span>{{ articleData.case_number }}</span>
            </div>
            <div v-if="articleData.decision_type" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.decisionType') }}</strong>
              <span>{{ articleData.decision_type }}</span>
            </div>
            <div v-if="articleData.decision_language" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.decisionLanguage') }}</strong>
              <span>{{ articleData.decision_language }}</span>
            </div>
            <div v-if="articleData.date" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.member_state" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.memberState') }}</strong>
              <span>{{ articleData.member_state }}</span>
            </div>
            <div v-if="articleData.companies" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.parties') }}</strong>
              <span>{{ articleData.companies }}</span>
            </div>
            <div v-if="articleData.legal_basis" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.legalBasis') }}</strong>
              <span>{{ articleData.legal_basis }}</span>
            </div>
            <div v-if="articleData.sectors" class="eccomp-info-item">
              <strong>{{ $t('modals.articleDetail.fields.sectors') }}</strong>
              <span>{{ articleData.sectors }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.eccompSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- UPC (Juridiction unifiée du brevet) -->
        <div class="details-content" v-else-if="articleData.source === 'upc'">
          <div class="upc-metadata">
            <div v-if="articleData.court" class="upc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.court') }}</strong>
              <span class="upc-court-badge">{{ articleData.court }}</span>
            </div>
            <div v-if="articleData.action_type" class="upc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.actionType') }}</strong>
              <span>{{ articleData.action_type }}</span>
            </div>
            <div v-if="articleData.case_numbers && articleData.case_numbers.length" class="upc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.caseNumber') }}</strong>
              <span>{{ articleData.case_numbers.join(', ') }}</span>
            </div>
            <div v-if="articleData.registry_number" class="upc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.registryNumber') }}</strong>
              <span>{{ articleData.registry_number }}</span>
            </div>
            <div v-if="articleData.date" class="upc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span>{{ articleData.date }}</span>
            </div>
            <div v-if="articleData.parties" class="upc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.parties') }}</strong>
              <span>{{ articleData.parties }}</span>
            </div>
            <div v-if="articleData.language_of_proceedings" class="upc-info-item">
              <strong>{{ $t('modals.articleDetail.fields.languageOfProceedings') }}</strong>
              <span>{{ articleData.language_of_proceedings }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.upcSummary') }}</h4>
          <div v-html="formattedContent"></div>
          <div v-if="articleData.headnotes" class="upc-headnotes">
            <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.upcHeadnotes') }}</h4>
            <div v-html="articleData.headnotes.replace(/\n/g, '<br>')"></div>
          </div>
          <div v-if="articleData.keywords" class="upc-keywords">
            <strong>{{ $t('modals.articleDetail.fields.keywords') }}</strong>
            <span>{{ articleData.keywords }}</span>
          </div>
        </div>

        <!-- EDPB (European Data Protection Board) -->
        <div class="details-content" v-else-if="articleData.source === 'edpb'">
          <div class="edpb-metadata">
            <div v-if="articleData.publication_types && articleData.publication_types.length" class="edpb-info-item">
              <strong>{{ $t('modals.articleDetail.fields.publicationType') }}</strong>
              <span class="edpb-type-badge">{{ articleData.publication_types.join(', ') }}</span>
            </div>
            <div v-if="articleData.topics && articleData.topics.length" class="edpb-info-item">
              <strong>{{ $t('modals.articleDetail.fields.topics') }}</strong>
              <span>{{ articleData.topics.join(', ') }}</span>
            </div>
            <div v-if="articleData.members && articleData.members.length" class="edpb-info-item">
              <strong>{{ $t('modals.articleDetail.fields.members') }}</strong>
              <span>{{ articleData.members.join(', ') }}</span>
            </div>
            <div v-if="articleData.date" class="edpb-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span>{{ articleData.date }}</span>
            </div>
            <div v-if="articleData.status" class="edpb-info-item">
              <strong>{{ $t('modals.articleDetail.fields.status') }}</strong>
              <span>{{ articleData.status }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.edpbSummary') }}</h4>
          <div v-html="formattedContent"></div>
          <div v-if="articleData.key_points" class="edpb-key-points">
            <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.edpbKeyPoints') }}</h4>
            <div v-html="articleData.key_points.replace(/\n/g, '<br>')"></div>
          </div>
          <div v-if="articleData.keywords" class="edpb-keywords">
            <strong>{{ $t('modals.articleDetail.fields.keywords') }}</strong>
            <span>{{ articleData.keywords }}</span>
          </div>
        </div>

        <!-- AGCM (Autorità Garante della Concorrenza e del Mercato) -->
        <div class="details-content" v-else-if="articleData.source === 'agcm'">
          <div class="agcm-metadata">
            <div v-if="articleData.date" class="agcm-info-item">
              <strong>{{ $t('modals.articleDetail.fields.dataIt') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.year" class="agcm-info-item">
              <strong>{{ $t('modals.articleDetail.fields.annoIt') }}</strong>
              <span>{{ articleData.year }}</span>
            </div>
            <div v-if="articleData.rid" class="agcm-info-item">
              <strong>{{ $t('modals.articleDetail.fields.riferimentoIt') }}</strong>
              <span>{{ articleData.rid }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.agcmSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- Giustizia Amministrativa -->
        <div class="details-content" v-else-if="articleData.source === 'giustizia_amministrativa'">
          <div class="ga-metadata">
            <div v-if="articleData.court" class="ga-info-item">
              <strong>{{ $t('modals.articleDetail.fields.giudice') }}</strong>
              <span>{{ articleData.court }}</span>
            </div>
            <div v-if="articleData.section" class="ga-info-item">
              <strong>{{ $t('modals.articleDetail.fields.sezioneIt') }}</strong>
              <span>{{ articleData.section }}</span>
            </div>
            <div v-if="articleData.tipo" class="ga-info-item">
              <strong>{{ $t('modals.articleDetail.fields.tipo') }}</strong>
              <span>{{ articleData.tipo }}</span>
            </div>
            <div v-if="articleData.date" class="ga-info-item">
              <strong>{{ $t('modals.articleDetail.fields.dataIt') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.anno" class="ga-info-item">
              <strong>{{ $t('modals.articleDetail.fields.annoIt') }}</strong>
              <span>{{ articleData.anno }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.giustiziaSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- Jurisprudence Europe Italy (CGUE/Tribunale UE in italiano) -->
        <div class="details-content" v-else-if="articleData.source === 'jurisprudence_europe_italy'">
          <div class="euit-metadata">
            <div v-if="articleData.court" class="euit-info-item">
              <strong>{{ $t('modals.articleDetail.fields.giudice') }}</strong>
              <span>{{ articleData.court }}</span>
            </div>
            <div v-if="articleData.decision_type" class="euit-info-item">
              <strong>{{ $t('modals.articleDetail.fields.tipoDecisioneIt') }}</strong>
              <span>{{ articleData.decision_type }}</span>
            </div>
            <div v-if="articleData.date" class="euit-info-item">
              <strong>{{ $t('modals.articleDetail.fields.dataIt') }}</strong>
              <span>{{ formatDate(articleData.date) }}</span>
            </div>
            <div v-if="articleData.celex" class="euit-info-item">
              <strong>{{ $t('modals.articleDetail.fields.celex') }}</strong>
              <span>{{ articleData.celex }}</span>
            </div>
            <div v-if="articleData.ecli" class="euit-info-item">
              <strong>{{ $t('modals.articleDetail.fields.ecli') }}</strong>
              <span>{{ articleData.ecli }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.giustiziaSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- EUR-Lex Italia (Regolamenti e Direttive UE consolidati) -->
        <div class="details-content" v-else-if="articleData.source === 'eurlex_italy'">
          <div class="eurlex-metadata">
            <div v-if="articleData.breadcrumb" class="eurlex-info-item eurlex-breadcrumb-row">
              <strong>{{ $t('modals.articleDetail.fields.struttura') }}</strong>
              <span>{{ articleData.breadcrumb }}</span>
            </div>
            <div v-if="articleData.article_heading" class="eurlex-info-item">
              <strong>{{ $t('modals.articleDetail.fields.articolo') }}</strong>
              <span>{{ articleData.article_heading }}<span v-if="articleData.article_title"> — {{ articleData.article_title }}</span></span>
            </div>
            <div v-if="articleData.act_title" class="eurlex-info-item">
              <strong>{{ $t('modals.articleDetail.fields.attoDiBase') }}</strong>
              <span>{{ articleData.act_title }}</span>
            </div>
            <div v-if="articleData.cons_celex" class="eurlex-info-item">
              <strong>{{ $t('modals.articleDetail.fields.celexConsolidato') }}</strong>
              <span>{{ articleData.cons_celex }}</span>
            </div>
            <div v-if="articleData.consolidation_date" class="eurlex-info-item">
              <strong>{{ $t('modals.articleDetail.fields.dataConsolidamento') }}</strong>
              <span>{{ articleData.consolidation_date }}</span>
            </div>
          </div>
          <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.eurlexSummary') }}</h4>
          <div v-html="formattedContent"></div>
        </div>

        <!-- Mention -->
        <div class="details-content" v-else-if="articleData.source === 'mention'">
          <div class="mention-metadata">
            <div v-if="articleData.type" class="mention-info-item">
              <strong>{{ $t('modals.articleDetail.fields.type') }}</strong>
              <span class="mention-type">{{ articleData.type }}</span>
            </div>
            <div v-if="articleData.jurisdiction" class="mention-info-item">
              <strong>{{ $t('modals.articleDetail.fields.jurisdiction') }}</strong>
              <span class="mention-jurisdiction">{{ articleData.jurisdiction }}</span>
            </div>
            <div v-if="articleData.date" class="mention-info-item">
              <strong>{{ $t('modals.articleDetail.fields.date') }}</strong>
              <span class="mention-date">{{ formatDate(articleData.date) }}</span>
            </div>
          </div>

          <div v-html="formattedContent"></div>
        </div>

        <!-- Juritext -->
        <div class="details-content" v-else-if="articleData.source === 'juritext'">
          <div class="juritext-summary-header">
            <h4 class="summary-section-title">{{ $t('modals.articleDetail.sections.juritextSummary') }}</h4>
            <button
              v-if="articleData.content"
              class="export-word-button-inline"
              @click="exportJurisprudenceToWord"
              :title="$t('modals.articleDetail.actions.exportSummaryWordTitle')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-file-earmark-word" viewBox="0 0 16 16">
                <path d="M5.485 6.879a.5.5 0 1 0-.97.242l1.5 6a.5.5 0 0 0 .967.01L8 9.402l1.018 3.73a.5.5 0 0 0 .967-.01l1.5-6a.5.5 0 0 0-.97-.242l-1.036 4.144-.997-3.655a.5.5 0 0 0-.964 0l-.997 3.655L5.485 6.88z"/>
                <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
              </svg>
              {{ $t('modals.articleDetail.actions.exportSummaryWord') }}
            </button>
          </div>
          <div v-html="formattedContent"></div>
        </div>

        <!-- Default content -->
        <div class="details-content" v-else>
          <div v-html="formattedContent"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatDate,
  formatText,
  formatHierarchy,
  formatEtat,
  getEtatClass,
  formatBaloCategory,
  formatEuritextTitle,
  getOfficialDecisionLink,
  getBaloOfficialLink,
  getActesSocietesFullUrl,
  hasReferences,
  hasCitations
} from '@/utils/documentFormatters';
import { Document, Paragraph, HeadingLevel, Packer } from 'docx';
import { saveAs } from 'file-saver';

// External link icon as a functional component
const ExternalLinkIcon = {
  functional: true,
  render(h) {
    return h('svg', {
      attrs: {
        xmlns: 'http://www.w3.org/2000/svg',
        width: '10',
        height: '10',
        fill: 'currentColor',
        viewBox: '0 0 16 16'
      },
      style: { marginLeft: '3px' }
    }, [
      h('path', {
        attrs: {
          'fill-rule': 'evenodd',
          d: 'M15 2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1zM0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5.854 8.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707z'
        }
      })
    ]);
  }
};

export default {
  name: 'ArticleDetailPanel',
  components: {
    ExternalLinkIcon
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    articleData: {
      type: Object,
      default: () => ({
        title: '',
        content: '',
        source: '',
        sourceData: null,
        id: null
      })
    }
  },
  emits: ['close', 'open-text-modal'],
  computed: {
    hasReferencesComputed() {
      // Si c'est une mention avec une URL, on force l'affichage
      if (this.articleData.source === 'mention' && this.articleData.url) {
        return true;
      }
      return hasReferences(this.articleData);
    },
    hasCitationsComputed() {
      return hasCitations(this.articleData);
    },
    formattedContent() {
      return formatText(this.articleData.content);
    },
    formattedAnnotation() {
      return formatText(this.articleData.annotation);
    },
    officialDecisionLink() {
      if (this.articleData.source === 'juritext' && this.articleData.url) {
        return this.articleData.url;
      }
      return getOfficialDecisionLink(this.articleData.id, this.articleData.title);
    },
    baloOfficialLink() {
      return getBaloOfficialLink(this.articleData.id_annonce, this.articleData.sourceData);
    },
    actesSocietesSourcePath() {
      // Priority 1: Direct source_path
      if (this.articleData.source_path) {
        return this.articleData.source_path;
      }

      // Priority 2: Check sourceData
      if (this.articleData.sourceData) {
        // FLASH mode: source_path exists
        if (this.articleData.sourceData.source_path) {
          return this.articleData.sourceData.source_path;
        }
        // Normal mode: source contains URL (not source name)
        if (this.articleData.sourceData.source &&
            this.articleData.sourceData.source !== 'actes_societes') {
          return this.articleData.sourceData.source;
        }
      }

      return null;
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },

    extractFollowUpQuestion(content) {
      // Vérifier si c'est une question de suivi
      if (content.includes('Question de suivi:') && content.includes('Contexte:')) {
        // Extraire seulement la partie après "Question de suivi:"
        const parts = content.split('Question de suivi:');
        if (parts.length > 1) {
          return parts[1].trim();
        }
      }
      // Sinon retourner le contenu complet
      return content;
    },

    async exportJurisprudenceToWord() {
      if (!this.articleData.content) {
        return;
      }

      try {
        const title = this.articleData.title || this.$t('modals.articleDetail.labels.defaultJuritextTitle');

        // Extraire seulement la question de suivi si c'est le cas
        const contentToExport = this.extractFollowUpQuestion(this.articleData.content);

        // Parse le summary en markdown simple et convertit en paragraphes Word
        const paragraphs = this.parseSummaryToDocx(contentToExport, title);

        // Créer le document Word
        const wordDoc = new Document({
          // Sans ce flag, Word étire sur toute la largeur les lignes justifiées
          // terminées par un saut de ligne manuel (<br> -> TextRun break:1)
          compatibility: { doNotExpandShiftReturn: true },
          sections: [{
            properties: {},
            children: paragraphs
          }]
        });

        // Générer le fichier
        const blob = await Packer.toBlob(wordDoc);

        // Créer un nom de fichier sanitizé
        const sanitizedTitle = title.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
        const filename = `${sanitizedTitle}_summary.docx`;

        // Télécharger
        saveAs(blob, filename);
      } catch (error) {
        console.error('Erreur lors de l\'export Word:', error);
        alert(this.$t('modals.articleDetail.toasts.wordExportError'));
      }
    },

    parseSummaryToDocx(summary, title) {
      const paragraphs = [];

      // Ajouter le titre
      paragraphs.push(
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_1,
          spacing: { after: 200 }
        })
      );

      // Ajouter un sous-titre
      paragraphs.push(
        new Paragraph({
          text: this.$t('modals.articleDetail.labels.decisionSummary'),
          heading: HeadingLevel.HEADING_2,
          spacing: { after: 200 }
        })
      );

      // Parser le contenu du summary
      const lines = summary.split('\n');

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        if (!line) {
          // Ligne vide
          paragraphs.push(new Paragraph({ text: '' }));
          continue;
        }

        // Détecter les titres markdown (## ou ###)
        if (line.startsWith('### ')) {
          paragraphs.push(
            new Paragraph({
              text: line.substring(4),
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 }
            })
          );
        } else if (line.startsWith('## ')) {
          paragraphs.push(
            new Paragraph({
              text: line.substring(3),
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 }
            })
          );
        } else if (line.startsWith('# ')) {
          paragraphs.push(
            new Paragraph({
              text: line.substring(2),
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 200, after: 100 }
            })
          );
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          // Liste à puces
          paragraphs.push(
            new Paragraph({
              text: line.substring(2),
              bullet: { level: 0 },
              spacing: { after: 100 }
            })
          );
        } else if (line.match(/^\d+\.\s/)) {
          // Liste numérotée
          const text = line.replace(/^\d+\.\s/, '');
          paragraphs.push(
            new Paragraph({
              text: text,
              numbering: { reference: 'default-numbering', level: 0 },
              spacing: { after: 100 }
            })
          );
        } else {
          // Paragraphe normal
          // Nettoyer les balises HTML simples si présentes
          const cleanText = line
            .replace(/<\/?strong>/g, '')
            .replace(/<\/?em>/g, '')
            .replace(/<\/?b>/g, '')
            .replace(/<\/?i>/g, '');

          paragraphs.push(
            new Paragraph({
              text: cleanText,
              spacing: { after: 100 }
            })
          );
        }
      }

      return paragraphs;
    },

    // Expose formatters as methods for template use
    formatDate,
    formatText,
    formatHierarchy,
    formatEtat,
    getEtatClass,
    formatBaloCategory,
    formatEuritextTitle,
    getActesSocietesFullUrl
  }
};
</script>

<!-- Global styles (non-scoped) to match LegalAnalysisModal -->
<style>
/* Ref section styles */
.ref-section {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
  font-size: 14px;
}

.ref-title {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
  color: #625CFB;
  font-weight: bold;
}

.ref-content {
  margin-bottom: 10px;
  color: #625CFB;
}

.ref-content a {
  color: #625CFB;
  text-decoration: none;
  font-weight: 600;
}

.ref-content a:hover {
  text-decoration: underline;
}

.ref-section::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

/* Citations section styles */
.citations-section {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
  font-size: 14px;
}

.citations-title {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 16px;
  color: #625CFB;
  font-weight: bold;
}

.citation-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.citation-item {
  background-color: #ffffff;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.citation-item strong {
  color: #333;
  display: block;
  margin-bottom: 8px;
}

.citation-text {
  font-style: italic;
  color: #555;
  margin: 0;
  line-height: 1.5;
  border-left: 3px solid #ccc;
  padding-left: 10px;
}

.citations-section::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 20px;
}

/* CNN metadata */
.cnn-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.cnn-status, .cnn-validity, .cnn-reference {
  margin-bottom: 8px;
  font-size: 14px;
}

.cnn-status strong, .cnn-validity strong, .cnn-reference strong {
  color: #625CFB;
  margin-right: 8px;
}

.cnn-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

/* État badges */
.etat-vigueur {
  color: #625CFB;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #d1fae5;
  border-radius: 12px;
  font-size: 12px;
}

.etat-vigueur-etendu {
  color: #059669;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #a7f3d0;
  border-radius: 12px;
  font-size: 12px;
}

.etat-perime {
  color: #f59e0b;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #fef3c7;
  border-radius: 12px;
  font-size: 12px;
}

.etat-abroge {
  color: #ef4444;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #fecaca;
  border-radius: 12px;
  font-size: 12px;
}

.etat-remplace {
  color: #8b5cf6;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #e9d5ff;
  border-radius: 12px;
  font-size: 12px;
}

.etat-modifie {
  color: #3b82f6;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #dbeafe;
  border-radius: 12px;
  font-size: 12px;
}

.etat-default {
  color: #6b7280;
  font-weight: 600;
  padding: 2px 8px;
  background-color: #f3f4f6;
  border-radius: 12px;
  font-size: 12px;
}

/* BALO styles */
.balo-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.balo-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.balo-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 120px;
}

.balo-company-name {
  font-weight: 600;
  color: #625CFB;
}

.balo-siren {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.balo-category-badge {
  color: #1e40af;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.balo-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

/* Hierarchy styles */
.hierarchy {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eaeaea;
  color: #625CFB;
  font-size: 0.95em;
}

/* Annotation styles */
.annotation-section {
  margin-top: 30px;
  padding: 15px;
  background-color: #f5f5ff;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.annotation-title {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 16px;
  color: #625CFB;
  font-weight: bold;
}

.annotation-content {
  line-height: 1.6;
  font-size: 14px;
}

.annotation-section::before {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-bottom: 15px;
}

/* Reglements styles */
.reglements-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #0369a1;
  border-radius: 0 6px 6px 0;
}

.reglements-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.reglements-info-item strong {
  color: #0369a1;
  margin-right: 8px;
  min-width: 120px;
}

.reglements-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #0369a1, transparent);
  margin-top: 15px;
}

/* BODACC styles */
.bodacc-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #7c2d12;
  border-radius: 0 6px 6px 0;
}

.bodacc-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.bodacc-info-item strong {
  color: #7c2d12;
  margin-right: 8px;
  min-width: 120px;
}

.bodacc-family-badge, .bodacc-type-badge {
  background-color: #fef7ed;
  color: #c2410c;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.bodacc-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #7c2d12, transparent);
  margin-top: 15px;
}

/* Actes Societes styles */
.actes-societes-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.actes-societes-info-item strong {
  margin-right: 8px;
  min-width: 150px;
}

.actes-societes-siren-value {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.actes-societes-date-full {
  font-weight: 600;
}

.actes-societes-summary-section {
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #7c3aed;
  border-radius: 0 6px 6px 0;
}

.summary-title {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 15px;
  color: #7c3aed;
  font-weight: bold;
}

.summary-content {
  line-height: 1.6;
  font-size: 14px;
  text-align: justify;
}

.actes-societes-content-section {
  margin-top: 20px;
}

.content-section-title {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 15px;
  color: #7c3aed;
  font-weight: bold;
}

.actes-societes-summary-section::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #7c3aed, transparent);
  margin-top: 15px;
}

/* Infofi styles */
.infofi-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #0d9488;
  border-radius: 0 6px 6px 0;
}

.infofi-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.infofi-info-item strong {
  color: #0d9488;
  margin-right: 8px;
  min-width: 150px;
}

.infofi-company-name, .infofi-doc-date {
  font-weight: 600;
}

.infofi-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #0d9488, transparent);
  margin-top: 15px;
}

/* Concurrence styles */
.concurrence-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 0 6px 6px 0;
}

.concurrence-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.concurrence-info-item strong {
  margin-right: 8px;
  min-width: 150px;
}

.concurrence-type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.concurrence-affaire-num {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.concurrence-date-full, .concurrence-sector-name {
  font-weight: 600;
}

.concurrence-affaire {
  font-family: 'Monaco', 'Menlo', monospace;
  font-weight: 600;
}

/* INPI styles */
.inpi-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-radius: 0 6px 6px 0;
}

.inpi-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.inpi-info-item strong {
  margin-right: 8px;
  min-width: 150px;
}

.inpi-domain-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.inpi-reference-num {
  font-family: 'Monaco', 'Menlo', monospace;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.inpi-date-full, .inpi-jurisdiction-name {
  font-weight: 600;
}

.inpi-reference {
  font-family: 'Monaco', 'Menlo', monospace;
  font-weight: 600;
}

/* CNIL styles */
.cnil-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #625CFB;
  border-radius: 0 6px 6px 0;
}

.cnil-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #625CFB, transparent);
  margin-top: 15px;
}

.cnil-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.cnil-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 130px;
}

.cnil-ref-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #625CFB;
}

.cnil-ref-item strong {
  color: #625CFB;
}

.cnil-nature {
  font-weight: 600;
  color: #625CFB;
}

.cnil-date-full {
  font-weight: 600;
}

.cnil-full-title {
  font-style: italic;
  flex: 1;
}

/* BOSS styles */
.boss-metadata {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: rgba(98, 92, 251, 0.04);
  border-radius: 8px;
}

.boss-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.boss-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 130px;
}

/* ACPR styles */
.acpr-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.acpr-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.acpr-info-item strong {
  color: #555;
  min-width: 90px;
}
.acpr-source-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 12px;
  font-weight: 500;
}
.acpr-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}

/* AMF styles */
.amf-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.amf-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.amf-info-item strong {
  color: #555;
  min-width: 90px;
}
.amf-source-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 12px;
  font-weight: 500;
}
.amf-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}

.eccomp-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.eccomp-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.eccomp-info-item strong {
  color: #555;
  min-width: 140px;
}
.eccomp-instrument-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 12px;
  font-weight: 500;
}
.eccomp-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}

.upc-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #625CFB;
}
.upc-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.upc-info-item strong {
  color: #555;
  min-width: 140px;
}
.upc-court-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #625CFB;
  color: white;
  font-size: 11px;
  font-weight: 500;
}
.upc-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}
.upc-headnotes {
  margin-top: 16px;
}
.upc-keywords {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f0eefc;
  border-radius: 6px;
  font-size: 12px;
  color: #555;
}
.upc-keywords strong {
  color: #625CFB;
  margin-right: 8px;
}

.edpb-metadata {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #003399;
}
.edpb-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
}
.edpb-info-item strong {
  color: #555;
  min-width: 140px;
}
.edpb-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #003399;
  color: white;
  font-size: 11px;
  font-weight: 500;
}
.edpb-ref-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
}
.edpb-key-points {
  margin-top: 16px;
}
.edpb-keywords {
  margin-top: 12px;
  padding: 8px 12px;
  background: #eaeffb;
  border-radius: 6px;
  font-size: 12px;
  color: #555;
}
.edpb-keywords strong {
  color: #003399;
  margin-right: 8px;
}

.boss-date-full {
  font-weight: 600;
}

/* Euritext links */
.euritext-links {
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 5px;
}

.euritext-link {
  margin-top: 8px;
}

.euritext-link a {
  color: #625CFB;
  text-decoration: underline;
}

/* Source URL */
.source-url {
  margin-bottom: 20px;
  border-radius: 5px;
}

.source-url a {
  color: #625CFB;
  text-decoration: none;
}

/* Decisions links */
.decisions-links {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.juritext-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  gap: 15px;
}

.summary-section-title {
  margin-bottom: 2em;
  font-size: 16px;
  color: #625CFB;
  font-weight: bold;
}

.export-word-button-inline {
  background: #625CFB;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.export-word-button-inline:hover {
  background-color: #4f46e5;
}

.export-word-button-inline svg {
  width: 14px;
  height: 14px;
}

/* Statuts styles */
.statuts-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.statuts-info-item strong {
  color: #625CFB;
  margin-right: 8px;
  min-width: 120px;
}

.statuts-link {
  text-decoration: none;
  font-weight: 600;
}

.statuts-link:hover {
  text-decoration: underline;
}

/* Mention styles */
.mention-metadata {
  margin-bottom: 20px;
  padding: 15px;
  border-left: 4px solid #6b21a8;
  border-radius: 0 6px 6px 0;
}

.mention-metadata::after {
  content: "";
  display: block;
  height: 1px;
  background: linear-gradient(to right, #6b21a8, transparent);
  margin-top: 15px;
}

.mention-info-item {
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.mention-info-item strong {
  color: #6b21a8;
  margin-right: 8px;
  min-width: 120px;
}

.mention-type {
  background-color: #f3e8ff;
  color: #6b21a8;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.mention-jurisdiction, .mention-date {
  font-weight: 600;
}

.official-link-section {
  margin-top: 20px;
  padding: 15px;
  text-align: center;
}

.official-link-button {
  display: inline-flex;
  align-items: center;
  padding: 12px 24px;
  background-color: #6b21a8;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

.official-link-button:hover {
  background-color: #7c3aed;
  text-decoration: none;
}
</style>

<style scoped>
/* Modal overlay - matching LegalAnalysisModal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Roboto', sans-serif;
}

/* Article details panel - matching LegalAnalysisModal */
.article-details {
  width: 70%;
  max-width: 800px;
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid #DFDFDF;
}

.bg-blue-color {
  background-color: #f2f2ff;
}

/* Details header - matching LegalAnalysisModal */
.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 15px;
  border-bottom: 1px solid #eaeaea;
  flex-shrink: 0;
  background-color: #f2f2ff;
}

.details-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  flex: 1;
  padding-right: 1rem;
  color: #333;
}

.close-details {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #666;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-details:hover {
  background-color: #e0e0e0;
  color: #333;
}

/* Scrollable details area - matching LegalAnalysisModal */
.scrollable-details-area {
  flex-grow: 1;
  overflow-y: auto;
  padding: 0;
}

/* Details content - matching LegalAnalysisModal */
.details-content {
  padding: 20px;
  font-size: 14px;
  text-align: justify;
  line-height: 1.6;
  color: #333;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .article-details {
    width: 95%;
    max-width: 100%;
    height: 90vh;
  }

  .details-header {
    padding: 1rem;
  }
}
</style>
